"use client";

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  Bell,
  Check,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { StrategyWaves, WAVE_KIND_BY_KEY } from "@/components/wheel/strategy-waves";
import {
  CUSTOM_WHEEL_STRATEGY,
  WHEEL_STRATEGY_CONFIGS,
  getWheelStrategyOptions,
  type WheelStrategyConfig,
  type WheelStrategyKey,
} from "@/lib/wheel-strategies";

const US_EQUITIES_WATCHLIST_ID = "cmaoz1cai0000f6hmecred40d";

// A collateral cap below this sentinel applies a strike filter (cap / 100);
// at or above it the codebase treats the cap as "no limit". See
// resolveStrategyEntryFilters in lib/wheel-strategies.ts.
const COLLATERAL_CAP_NO_LIMIT = 50000;
const COLLATERAL_CAP_MIN = 1000;
const COLLATERAL_CAP_MAX = 49000;
const COLLATERAL_CAP_DEFAULT = 10000;

function formatCollateral(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
  return `$${value}`;
}

type AccentStyle = CSSProperties & {
  "--accent"?: string;
};

type StrategyApiResponse = {
  activeWheelStrategy: WheelStrategyKey;
  strategy: {
    key: WheelStrategyKey;
    label: string;
    shortLabel: string;
    description: string;
    signatureClassName: string;
    modalCopy: {
      summary: string;
      entryAlertStrategy: string;
      rollSuggestions: string;
      coveredCallTargeting: string;
      riskAlerts: string;
      workflowPriorities: string;
    };
  };
};

interface WheelStrategyPickerProps {
  compact?: boolean;
  className?: string;
  /** Custom trigger renderer. Receives an `open` callback to launch the dialog.
   *  When provided, replaces the default strategy button (used for the inline
   *  mini-picker in the Find Deals header). */
  renderTrigger?: (open: () => void) => ReactNode;
}

// Meters are derived straight from each strategy's real config so the bars stay
// honest: risk from max assignment delta, trade frequency from the DTE window +
// expiration warning, and yield from the minimum yearly-yield target.
function computeMeters(config: WheelStrategyConfig) {
  const ef = config.entryFilters;
  if (!ef) return { risk: 50, freq: 50, yield: 50 };
  const clamp = (n: number) => Math.max(8, Math.min(100, Math.round(n)));
  const maxDelta = ef.riskOfAssignmentFilter[1];
  const maxDte = ef.daysFilter[1];
  const expDte = config.positionAlerts.expirationDte;
  const waprMin = ef.waprFilter[0];
  return {
    risk: clamp((maxDelta / 0.4) * 100),
    freq: clamp((1 - maxDte / 60) * 70 + (1 - expDte / 10) * 30),
    yield: clamp((waprMin / 40) * 100),
  };
}

export function WheelStrategyPicker({ compact = false, className, renderTrigger }: WheelStrategyPickerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [activeStrategy, setActiveStrategy] = useState<WheelStrategyKey>(CUSTOM_WHEEL_STRATEGY);
  const [selectedStrategy, setSelectedStrategy] = useState<WheelStrategyKey>(CUSTOM_WHEEL_STRATEGY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [capEnabled, setCapEnabled] = useState(false);
  const [capValue, setCapValue] = useState(COLLATERAL_CAP_DEFAULT);
  const [capSaving, setCapSaving] = useState(false);
  const lastPersistedCap = useRef(COLLATERAL_CAP_NO_LIMIT);
  const capSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const strategies = useMemo(() => getWheelStrategyOptions(), []);
  const activeConfig = WHEEL_STRATEGY_CONFIGS[activeStrategy] ?? WHEEL_STRATEGY_CONFIGS[CUSTOM_WHEEL_STRATEGY];
  const selectedConfig = WHEEL_STRATEGY_CONFIGS[selectedStrategy] ?? WHEEL_STRATEGY_CONFIGS[CUSTOM_WHEEL_STRATEGY];

  useEffect(() => {
    let cancelled = false;
    async function loadStrategy() {
      try {
        const res = await fetch("/api/wheel/strategy", { cache: "no-store" });
        if (!res.ok) return;
        const body = (await res.json()) as StrategyApiResponse;
        if (cancelled) return;
        setActiveStrategy(body.activeWheelStrategy);
        setSelectedStrategy(body.activeWheelStrategy);
      } catch {
        // Strategy control should never block navigation.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadStrategy();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadCollateralCap() {
      try {
        const res = await fetch("/api/user/preferences", { cache: "no-store" });
        if (!res.ok) return;
        const body = await res.json();
        if (cancelled) return;
        const raw = typeof body.maxCollateralPerTrade === "number" ? body.maxCollateralPerTrade : COLLATERAL_CAP_NO_LIMIT;
        lastPersistedCap.current = raw;
        if (raw > 0 && raw < COLLATERAL_CAP_NO_LIMIT) {
          setCapEnabled(true);
          setCapValue(Math.min(COLLATERAL_CAP_MAX, Math.max(COLLATERAL_CAP_MIN, raw)));
        } else {
          setCapEnabled(false);
        }
      } catch {
        // Collateral cap control should never block the picker.
      }
    }
    loadCollateralCap();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistCollateralCap = async (nextValue: number) => {
    if (nextValue === lastPersistedCap.current) return;
    setCapSaving(true);
    try {
      const res = await fetch("/api/user/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxCollateralPerTrade: nextValue }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || "Failed to update collateral cap");
      }
      lastPersistedCap.current = nextValue;
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update collateral cap");
    } finally {
      setCapSaving(false);
    }
  };

  // Debounce slider drags so the final value always persists, even when the
  // user makes several quick adjustments in a row.
  const scheduleCapSave = (nextValue: number) => {
    if (capSaveTimer.current) clearTimeout(capSaveTimer.current);
    capSaveTimer.current = setTimeout(() => {
      void persistCollateralCap(nextValue);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (capSaveTimer.current) clearTimeout(capSaveTimer.current);
    };
  }, []);

  const handleCapToggle = (checked: boolean) => {
    if (capSaveTimer.current) clearTimeout(capSaveTimer.current);
    setCapEnabled(checked);
    void persistCollateralCap(checked ? capValue : COLLATERAL_CAP_NO_LIMIT);
  };

  const applyStrategy = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/wheel/strategy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ strategy: selectedStrategy }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || "Failed to apply strategy");
      }
      const body = (await res.json()) as StrategyApiResponse;
      setActiveStrategy(body.activeWheelStrategy);
      setSelectedStrategy(body.activeWheelStrategy);
      window.dispatchEvent(new CustomEvent("wheel-strategy-updated", {
        detail: { strategy: body.activeWheelStrategy },
      }));
      if (pathname === "/dashboard/wheel/find-deals") {
        router.replace(`/dashboard/wheel/find-deals?watchlistId=${US_EQUITIES_WATCHLIST_ID}&strategyApplied=${Date.now()}`);
      }
      router.refresh();
      toast.success(`${body.strategy.label} strategy applied`, {
        description: "Wheel defaults updated. Your custom settings are preserved for Custom.",
      });
      setOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to apply strategy");
    } finally {
      setSaving(false);
    }
  };

  const selectedIsCustom = selectedStrategy === CUSTOM_WHEEL_STRATEGY;

  const changeRows: Array<{ icon: typeof Search; label: string; copy: string }> = [
    { icon: Search, label: "Find Deals", copy: selectedConfig.modalCopy.entryAlertStrategy },
    { icon: RefreshCw, label: "Roll", copy: selectedConfig.modalCopy.rollSuggestions },
    { icon: TrendingUp, label: "Covered Calls", copy: selectedConfig.modalCopy.coveredCallTargeting },
    {
      icon: Bell,
      label: "Alerts",
      copy: selectedIsCustom
        ? "Uses your existing alert settings."
        : "Email + Discord alerts are enabled for this strategy.",
    },
  ];

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setOpen(true))
      ) : (
        <Button
          type="button"
          variant="outline"
          size={compact ? "sm" : "default"}
          data-trial-highlight="strategy-picker"
          className={cn(
            "w-full justify-between border-border/60 bg-gradient-to-r from-zinc-100/60 via-background to-zinc-100/40 text-left dark:from-zinc-900/80 dark:via-background dark:to-zinc-900/60",
            className
          )}
          onClick={() => setOpen(true)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-full">
              <StrategyWaves kind={WAVE_KIND_BY_KEY[activeStrategy]} size={18} />
            </span>
            <span className="truncate text-xs">
              {loading ? "Strategy..." : activeConfig.label}
            </span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl overflow-hidden border-border/60 p-0">
          <div className="h-px bg-gradient-to-r from-transparent via-zinc-300 to-transparent dark:via-zinc-600" />
          <div className="max-h-[85vh] overflow-y-auto p-6">
            <DialogHeader>
              <DialogTitle className="text-2xl font-medium">Choose your Wheel strategy</DialogTitle>
              <DialogDescription>
                A strategy is a pre-packaged set of filters and settings that QuantWheel uses to{" "}
                <span className="text-foreground">find deals</span>, suggest{" "}
                <span className="text-foreground">rolls</span> and{" "}
                <span className="text-foreground">covered calls</span>, and warn you about{" "}
                <span className="text-foreground">risk</span>. Switching never deletes your settings or progress, you can always come back to your custom settings.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 grid gap-3 md:grid-cols-4">
              {strategies.map((strategy) => {
                const selected = selectedStrategy === strategy.key;
                const active = activeStrategy === strategy.key;
                const meters = computeMeters(strategy);
                const targetYield = strategy.entryFilters?.waprFilter?.[0] ?? null;
                const meterRows: Array<{ label: string; value: number; display: string }> = [
                  { label: "Risk", value: meters.risk, display: String(meters.risk) },
                  { label: "Trade Freq.", value: meters.freq, display: String(meters.freq) },
                  {
                    label: "Est. Yield",
                    value: meters.yield,
                    display: targetYield != null ? `${targetYield}%` : String(meters.yield),
                  },
                ];
                return (
                  <button
                    key={strategy.key}
                    type="button"
                    onClick={() => setSelectedStrategy(strategy.key)}
                    style={{ "--accent": strategy.accent } as AccentStyle}
                    className={cn(
                      "group relative min-h-[150px] rounded-2xl border p-4 text-left transition-all duration-200",
                      "hover:-translate-y-1",
                      selected
                        ? "border-transparent bg-card shadow-[0_0_0_1.5px_var(--accent)]"
                        : "border-border/60 bg-card/40 hover:border-border"
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "pointer-events-none absolute inset-x-0 top-0 h-28 rounded-t-2xl transition-opacity duration-300",
                        selected ? "opacity-[0.45]" : "opacity-0 group-hover:opacity-[0.5]"
                      )}
                      style={{
                        background:
                          "radial-gradient(130% 85% at 50% -10%, var(--accent), transparent 60%)",
                      }}
                    />
                    {active && (
                      <span className="absolute left-3 top-3 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">
                        Current
                      </span>
                    )}
                    {selected && (
                      <span
                        className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full text-[11px] font-bold text-background"
                        style={{ backgroundColor: strategy.accent }}
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    )}

                    <span className="relative mt-2 flex justify-center">
                      <StrategyWaves kind={WAVE_KIND_BY_KEY[strategy.key]} size={64} />
                    </span>
                    <p className="mt-3 text-center text-sm font-medium">{strategy.label}</p>
                    <p className="mx-auto mt-2 min-h-[48px] text-center text-[10px] leading-relaxed text-muted-foreground">
                      {strategy.description}
                    </p>

                    <div className="mt-3 grid gap-1.5">
                      {meterRows.map(({ label, value, display }) => (
                        <div key={label} className="flex items-center gap-2">
                          <span className="w-14 text-[9px] uppercase tracking-wide text-muted-foreground">
                            {label}
                          </span>
                          <span className="h-1 flex-1 overflow-hidden rounded-full bg-muted/60">
                            <span
                              className="block h-full rounded-full"
                              style={{ width: `${value}%`, backgroundColor: strategy.accent, opacity: 0.85 }}
                            />
                          </span>
                          <span className="w-9 text-right text-[10px] tabular-nums text-muted-foreground">
                            {display}
                          </span>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            <div
              key={selectedStrategy}
              style={{ "--accent": selectedConfig.accent } as AccentStyle}
              className="mt-4 overflow-hidden rounded-2xl border border-border/60 bg-card/70 duration-300 animate-in fade-in slide-in-from-bottom-1"
            >
              <div className="h-px bg-gradient-to-r from-transparent via-zinc-300/70 to-transparent dark:via-zinc-700/70" />
              <div className="px-5 py-3">
                <h4 className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  What changes across the app
                </h4>
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {changeRows.map(({ icon: Icon, label, copy }) => (
                    <div
                      key={label}
                      className="rounded-lg border border-border/60 bg-background/40 p-2.5 text-[11px]"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md border border-border/60 bg-background/40 text-muted-foreground">
                          <Icon className="h-3 w-3" />
                        </span>
                        <p className="font-medium">{label}</p>
                      </div>
                      <p className="mt-1.5 leading-snug text-muted-foreground">{copy}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-border/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Limit collateral per trade</p>
                    <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">
                      Caps Find Deals strike prices so a single contract never ties up more than your limit.
                    </p>
                  </div>
                  <Switch
                    checked={capEnabled}
                    disabled={capSaving}
                    onCheckedChange={handleCapToggle}
                    aria-label="Limit collateral per trade"
                  />
                </div>
                {capEnabled && (
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[capValue]}
                      min={COLLATERAL_CAP_MIN}
                      max={COLLATERAL_CAP_MAX}
                      step={1000}
                      onValueChange={(value) => {
                        setCapValue(value[0]);
                        scheduleCapSave(value[0]);
                      }}
                      className="max-w-xs flex-1"
                    />
                    <span className="min-w-[10ch] text-xs text-muted-foreground">
                      {formatCollateral(capValue)} · ≤ ${(capValue / 100).toFixed(0)} strike
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 border-t border-border/60 p-4 sm:flex-row sm:items-center">
                <p className="text-[10px] leading-relaxed text-muted-foreground sm:max-w-md">
                  Presets update Wheel defaults without deleting progress. Target bands are
                  illustrative goals based on configuration, not guaranteed returns. GEX is not
                  included yet.
                </p>
                <div className="flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row">
                  <Button type="button" variant="outline" disabled>
                    Show performance · soon
                  </Button>
                  <Button
                    type="button"
                    onClick={applyStrategy}
                    disabled={saving || selectedStrategy === activeStrategy}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Apply {selectedConfig.label}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
